/**
 * Server validation, field by field (wave W1.5, Mahmoud's walk finding 2).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * He filled the Create project dialog, pressed Register project and read only "The
 * request failed validation in 1 place(s)". Nothing was marked. The server now names each
 * place (`errors[]`: a dotted field path and a plain sentence) and this module is the ONE
 * way a form reads it:
 *
 * - `fieldError(error, "contract.title")` is the inline message for one control.
 * - `isFieldInvalid(error, "contract.title")` drives its `aria-invalid`.
 * - `validationSummary(error, labels)` is the sentence above the buttons: it names every
 *   field in plain words, the labels the form gives it first, a readable form of the
 *   server's own path for anything the form has no control for.
 * - `ServerFieldError` renders the inline line under a control.
 *
 * The forms gate (`src/lib/serverErrors.test.ts`) fails when a screen that sends a write
 * renders its refusals without going through this module.
 */
import {
  cloneElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError, type FieldProblem } from "../api/client";
import { strings } from "../brand/strings";

/** The per-field places a refusal carries, or none. */
export function fieldProblems(error: unknown): readonly FieldProblem[] {
  if (!(error instanceof ApiError)) {
    return [];
  }
  return error.problem?.errors ?? [];
}

/** A server path belongs to a form field when it IS the field or sits inside it. */
function belongsTo(path: string, field: string): boolean {
  return path === field || path.startsWith(`${field}.`);
}

/** The inline message for one form field, or null. */
export function fieldError(error: unknown, field: string): string | null {
  return fieldProblems(error).find((one) => belongsTo(one.field, field))?.message ?? null;
}

export function isFieldInvalid(error: unknown, field: string): boolean {
  return fieldError(error, field) !== null;
}

/**
 * Map the server's places onto a form's own keys: `{ "contract.title": "contractTitle" }`.
 * Returns the message per form key (the first place wins).
 */
export function fieldErrorsFor<K extends string>(
  error: unknown,
  map: Readonly<Record<string, K>>,
): Partial<Record<K, string>> {
  const found: Partial<Record<K, string>> = {};
  for (const problem of fieldProblems(error)) {
    const field = Object.keys(map)
      .filter((path) => belongsTo(problem.field, path))
      .sort((a, b) => b.length - a.length)[0];
    if (field === undefined) {
      continue;
    }
    const key = map[field];
    if (key !== undefined && found[key] === undefined) {
      found[key] = problem.message;
    }
  }
  return found;
}

/** A server path in plain words: its last named segment, spaces for underscores. */
export function humanField(path: string): string {
  const named = path.split(".").filter((part) => part !== "" && !/^\d+$/.test(part));
  const last = named[named.length - 1] ?? path;
  return last
    .replace(/_ids?$/, "")
    .replace(/_/g, " ")
    .trim();
}

/**
 * The sentence that names every refused field in plain words. Labels the form passes
 * win; a field the form has no label for is named from its path, with its message, so
 * nothing the server refused is left unsaid.
 */
export function validationSummary(
  error: unknown,
  labels: Readonly<Record<string, string>> = {},
): string | null {
  const problems = fieldProblems(error);
  if (problems.length === 0) {
    return null;
  }
  const names: string[] = [];
  for (const problem of problems) {
    const labelled = Object.keys(labels)
      .filter((path) => belongsTo(problem.field, path))
      .sort((a, b) => b.length - a.length)[0];
    const name =
      labelled === undefined
        ? `${humanField(problem.field)} (${problem.message.replace(/\.$/, "").toLowerCase()})`
        : (labels[labelled] ?? humanField(problem.field));
    if (!names.includes(name)) {
      names.push(name);
    }
  }
  return strings.common.validationSummary(names);
}

/**
 * The refusal text for any form: the per-field summary when the server named fields,
 * otherwise the server's own sentence (or the transport's).
 */
export function refusalText(error: unknown, labels: Readonly<Record<string, string>> = {}): string {
  const summary = validationSummary(error, labels);
  if (summary !== null) {
    return summary;
  }
  if (error instanceof ApiError) {
    const detail = error.problem?.detail ?? "";
    return detail !== "" ? detail : error.message;
  }
  return strings.common.failed;
}

/**
 * The refusal a page's forms are currently showing. A house screen keeps one refusal per
 * page (its action error); wrapping the page in ServerErrorScope lets every control
 * under it read its own field's message without threading the error through each part.
 */
const ServerErrorContext = createContext<unknown>(null);

/**
 * Wave W2: the fields a scope's forms actually render. Every ServerField registers its
 * field while it is mounted, so a scope knows when the server named a field the open
 * form has no control for (Mahmoud's walk: "Plan the activity" failed naming "Activity
 * type", a field that was not on the form). Such a refusal is still said, in the
 * dialog, and in development it is reported loudly.
 */
interface FieldRegistry {
  readonly fields: readonly string[];
  readonly register: (field: string) => () => void;
}

const FieldRegistryContext = createContext<FieldRegistry | null>(null);

export function ServerErrorScope({
  error,
  children,
}: {
  readonly error: unknown;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  const [fields, setFields] = useState<readonly string[]>([]);
  const register = useCallback((field: string): (() => void) => {
    setFields((current) => [...current, field]);
    return () => {
      setFields((current) => {
        const at = current.indexOf(field);
        return at < 0 ? current : [...current.slice(0, at), ...current.slice(at + 1)];
      });
    };
  }, []);
  const registry = useMemo(() => ({ fields, register }), [fields, register]);
  return (
    <ServerErrorContext.Provider value={error}>
      <FieldRegistryContext.Provider value={registry}>{children}</FieldRegistryContext.Provider>
    </ServerErrorContext.Provider>
  );
}

/**
 * The places a refusal names that no mounted control of the nearest scope can show.
 * Outside a scope there is no form to compare against, so nothing is reported.
 */
export function unplacedProblems(
  error: unknown,
  mounted: readonly string[] | null,
): readonly FieldProblem[] {
  if (mounted === null) {
    return [];
  }
  return fieldProblems(error).filter(
    (problem) => !mounted.some((field) => belongsTo(problem.field, field)),
  );
}

export function useUnplacedProblems(error: unknown): readonly FieldProblem[] {
  const registry = useContext(FieldRegistryContext);
  const unplaced = unplacedProblems(error, registry === null ? null : registry.fields);
  useEffect(() => {
    if (unplaced.length > 0 && import.meta.env.DEV) {
      console.error(
        `server refused field(s) the open form has no control for: ${unplaced
          .map((one) => one.field)
          .join(", ")}`,
      );
    }
  }, [unplaced]);
  return unplaced;
}

/** Register one field with the nearest scope while the control is mounted. */
function useRegisteredField(field: string): void {
  const registry = useContext(FieldRegistryContext);
  const register = registry?.register;
  useEffect(() => (register === undefined ? undefined : register(field)), [register, field]);
}

/** The field's server message and its aria-invalid, from the nearest scope. */
export function useServerField(field: string): {
  readonly message: string | null;
  readonly invalid: boolean;
} {
  const error = useContext(ServerErrorContext);
  const message = fieldError(error, field);
  return { message, invalid: message !== null };
}

/**
 * The inline line under a control, the same furniture as FieldError. The error comes
 * from the prop, or from the nearest ServerErrorScope when the prop is omitted.
 */
export function ServerFieldError({
  error,
  field,
  id,
}: {
  readonly error?: unknown;
  readonly field: string;
  readonly id: string;
}): React.JSX.Element | null {
  const scoped = useContext(ServerErrorContext);
  const message = fieldError(error === undefined ? scoped : error, field);
  if (message === null) {
    return null;
  }
  // The house class: it reads the same on a migrated form and on a house one.
  return (
    <p className="q-field__error" id={id} role="alert" data-testid={id}>
      {message}
    </p>
  );
}

/**
 * Wrap ONE control so the server's refusal of its field is marked on it and said under
 * it: `aria-invalid`, `aria-describedby` and the inline line. The house screens use this
 * so every form reads server validation the same way as the foundation forms do.
 */
export function ServerField({
  field,
  error,
  children,
}: {
  readonly field: string;
  readonly error?: unknown;
  readonly children: React.ReactElement<{
    id?: string;
    "aria-invalid"?: boolean;
    "aria-describedby"?: string;
  }>;
}): React.JSX.Element {
  const scoped = useContext(ServerErrorContext);
  useRegisteredField(field);
  const message = fieldError(error === undefined ? scoped : error, field);
  const base = children.props.id ?? field.replace(/\W+/g, "-");
  const errorId = `${base}-server-error`;
  return (
    <>
      {message === null
        ? children
        : cloneElement(children, { "aria-invalid": true, "aria-describedby": errorId })}
      {message !== null && (
        <p className="q-field__error" id={errorId} role="alert" data-testid={errorId}>
          {message}
        </p>
      )}
    </>
  );
}
